import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { Collapse } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

import CashCategoryServices from "../../../services/CashCategoryServices";
import Pagination from "../Pagenation/Pagenation";


const cashTypeOptions = [
  { value: "in", label: "In Cash" },
  { value: "out", label: "Out Cash" },
  { value: "both", label: "Both" }
];

const cashTypeLabels = {
  in: "In Cash",
  out: "Out Cash",
  both: "Both"
};

const readData = (response) => {
  const body = response?.data?.status
    ? response.data
    : response;

  return body?.data;
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const CashCategoryManagement = () => {
  const [addOpen, setAddOpen] = useState(true);
  const [listOpen, setListOpen] = useState(true);

  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [cashType, setCashType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [cashTypeFilter, setCashTypeFilter] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setLoadError(false);

    try {
      const response =
        await CashCategoryServices.getCashCategories();

      const data = readData(response);

      if (!Array.isArray(data?.categories)) {
        throw new Error("Unexpected category API response.");
      }

      setCategories(data.categories);
    } catch (error) {
      setLoadError(true);

      await Swal.fire(
        "Error",
        errorMessage(error, "Unable to load cash categories."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, cashTypeFilter, pageSize]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving) return;

    if (!name.trim() || !cashType) {
      await Swal.fire(
        "Required",
        "Please enter a category name and select a cash type.",
        "warning"
      );
      return;
    }

    setSaving(true);

    try {
      await CashCategoryServices.createCashCategory({
        name: name.trim(),
        cashType
      });
    } catch (error) {
      setSaving(false);

      await Swal.fire(
        "Error",
        errorMessage(error, "Unable to create cash category."),
        "error"
      );

      return;
    }

    await Swal.fire({
      icon: "success",
      title: "Success",
      text: "Cash category created successfully.",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false
    });

    setName("");
    setCashType("");
    setSearchTerm("");
    setStatusFilter("");
    setCashTypeFilter("");
    setPage(0);
    setListOpen(true);
    setSaving(false);

    await fetchCategories();
  };

  const filteredCategories = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return categories.filter((category) => {
      if (statusFilter && category.status !== statusFilter) {
        return false;
      }

      if (cashTypeFilter && category.cashType !== cashTypeFilter) {
        return false;
      }

      const text = [
        category.name,
        cashTypeLabels[category.cashType],
        category.createdBy?.name
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return !search || text.includes(search);
    });
  }, [categories, searchTerm, statusFilter, cashTypeFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCategories.length / pageSize)
  );

  const currentPage = Math.min(page, totalPages - 1);
  const start = currentPage * pageSize;

  const visibleCategories = filteredCategories.slice(
    start,
    start + pageSize
  );

  const handlePageChange = (nextPage) => {
    const value = Number(nextPage);

    if (!Number.isInteger(value)) return;

    setPage(
      Math.min(Math.max(0, value), totalPages - 1)
    );
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="row page-titles">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              Cash Management
            </li>
            <li className="breadcrumb-item active">
              Cash Categories
            </li>
          </ol>
        </div>

        <div className="row">
          {/* Add Category */}
          <div className="col-xl-4">
            <div className="filter cm-content-box box-primary">
              <div className="content-title">
                <div className="cpa">
                  <i
                    className="fa-solid fa-file-lines me-1"
                    aria-hidden="true"
                  />
                  Add Cash Category
                </div>

                <div className="tools">
                  <Link
                    to="#"
                    className={`SlideToolHeader ${addOpen ? "collapse" : "expand"
                      }`}
                    onClick={(event) => {
                      event.preventDefault();
                      setAddOpen((previous) => !previous);
                    }}
                    aria-label="Toggle category form"
                    aria-expanded={addOpen}
                  >
                    <i className="fas fa-angle-up" />
                  </Link>
                </div>
              </div>

              <Collapse in={addOpen}>
                <div className="cm-content-body form excerpt">
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>
                      <div className="mb-3">
                        <label
                          htmlFor="cash-category-name"
                          className="form-label"
                        >
                          Category Name
                        </label>

                        <input
                          id="cash-category-name"
                          type="text"
                          className="form-control"
                          value={name}
                          onChange={(event) =>
                            setName(event.target.value)
                          }
                          placeholder="Enter category name"
                          disabled={saving}
                        />
                      </div>

                      <div className="mb-3">
                        <label
                          htmlFor="cash-category-type"
                          className="form-label"
                        >
                          Cash Type
                        </label>

                        <Select
                          inputId="cash-category-type"
                          options={cashTypeOptions}
                          value={
                            cashTypeOptions.find(
                              (option) =>
                                option.value === cashType
                            ) || null
                          }
                          onChange={(selected) =>
                            setCashType(selected?.value || "")
                          }
                          placeholder="Select Cash Type"
                          isSearchable
                          isClearable
                          isDisabled={saving}
                          className="custom-react-select"
                          menuPlacement="auto"
                        />
                      </div>

                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                    </form>
                  </div>
                </div>
              </Collapse>
            </div>
          </div>

          {/* Category List */}
          <div className="col-xl-8">
            <div className="filter cm-content-box box-primary">
              <div className="content-title">
                <div className="cpa">
                  <i
                    className="fa-solid fa-file-lines me-1"
                    aria-hidden="true"
                  />
                  Cash Category List

                  <span className="badge bg-orange ms-2">
                    {filteredCategories.length}
                  </span>
                </div>

                <div className="tools">
                  <Link
                    to="#"
                    className={`SlideToolHeader ${listOpen ? "collapse" : "expand"
                      }`}
                    onClick={(event) => {
                      event.preventDefault();
                      setListOpen((previous) => !previous);
                    }}
                    aria-label="Toggle category list"
                    aria-expanded={listOpen}
                  >
                    <i className="fas fa-angle-up" />
                  </Link>
                </div>
              </div>

              <Collapse in={listOpen}>
                <div className="cm-content-body publish-content form excerpt">
                  <div className="card-body">
                    <div className="row g-2 mb-3">
                      <div className="col-12 col-md-6 col-lg-4">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search category..."
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                          aria-label="Search categories"
                        />
                      </div>

                      <div className="col-12 col-sm-6 col-md-6 col-lg-3">
                        <select
                          className="form-control"
                          value={statusFilter}
                          onChange={(event) => setStatusFilter(event.target.value)}
                          aria-label="Filter category status"
                        >
                          <option value="">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      <div className="col-12 col-sm-6 col-md-6 col-lg-3">
                        <select
                          className="form-control"
                          value={cashTypeFilter}
                          onChange={(event) => setCashTypeFilter(event.target.value)}
                          aria-label="Filter cash type"
                        >
                          <option value="">All Cash Types</option>
                          {cashTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12 col-sm-6 col-md-6 col-lg-2">
                        <select
                          className="form-control"
                          value={pageSize}
                          onChange={(event) =>
                            setPageSize(Number(event.target.value))
                          }
                          aria-label="Rows per page"
                        >
                          {[5, 10, 20, 50, 100].map((value) => (
                            <option key={value} value={value}>
                              {value} Rows
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-bordered table-striped verticle-middle table-responsive-sm">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Category Name</th>
                            <th>Cash Type</th>
                            <th>Created By</th>
                            <th>Created On</th>
                            <th>Status</th>
                          </tr>
                        </thead>

                        <tbody>
                          {loading ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="text-center py-4"
                              >
                                Loading categories...
                              </td>
                            </tr>
                          ) : loadError ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="text-center"
                              >
                                Unable to load categories.{" "}
                                <button
                                  type="button"
                                  className="btn btn-primary btn-xs"
                                  onClick={fetchCategories}
                                >
                                  Retry
                                </button>
                              </td>
                            </tr>
                          ) : visibleCategories.length === 0 ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="text-center"
                              >
                                No Record Found
                              </td>
                            </tr>
                          ) : (
                            visibleCategories.map(
                              (category, index) => (
                                <tr key={category._id}>
                                  <td>{start + index + 1}</td>
                                  <td>{category.name}</td>
                                  <td>
                                    {cashTypeLabels[
                                      category.cashType
                                    ] || "-"}
                                  </td>
                                  <td>
                                    {category.createdBy?.name || "-"}
                                  </td>
                                  <td>
                                    {category.createdAt
                                      ? new Date(
                                        category.createdAt
                                      ).toLocaleDateString("en-IN")
                                      : "-"}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${category.status === "active"
                                        ? "bg-success"
                                        : "bg-danger"
                                        }`}
                                    >
                                      {category.status === "active"
                                        ? "Active"
                                        : "Inactive"}
                                    </span>
                                  </td>
                                </tr>
                              )
                            )
                          )}
                        </tbody>
                      </table>
                    </div>

                    {!loading && !loadError && (
                      <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                        <div className="dataTables_info">
                          Showing{" "}
                          {filteredCategories.length
                            ? start + 1
                            : 0}{" "}
                          to{" "}
                          {Math.min(
                            start + pageSize,
                            filteredCategories.length
                          )}{" "}
                          of {filteredCategories.length} entries
                        </div>

                        <Pagination
                          activePage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Collapse>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashCategoryManagement;